// DOM UI: start overlay (progress, name, colour, Enter), controls hint, play button, network status. Kept small and outside the render loop.

import { cleanText, MAX_NAME_LENGTH } from '@lr3d/shared';

const $ = <T extends HTMLElement>(id: string) => document.getElementById(id) as T;

/**
 * Default names for a new visitor: one of these at random, followed by a number (e.g. "Test subject 214").
 * Edit this list to change the choices (user, 2026-10-28; more to come).
 */
export const DEFAULT_NAMES = ['Test subject', 'Observer', 'Practitioner'];

/** Avatar colours (2026-10-23): a family around a brown master hue (warm browns, tans, terracotta, olive, a cream,
 *  a near-black brown). Names are the radio buttons' labels. */
export const PALETTE: readonly { name: string; hex: string }[] = [
  { name: 'Espresso', hex: '#3b2a22' },
  { name: 'Umber', hex: '#6b4530' },
  { name: 'Chestnut', hex: '#93573a' },
  { name: 'Terracotta', hex: '#c4683f' },
  { name: 'Caramel', hex: '#c08a3e' },
  { name: 'Tan', hex: '#d2b183' },
  { name: 'Olive', hex: '#7d7142' },
  { name: 'Cream', hex: '#efe3cb' },
];
export const COLORS = PALETTE.map((c) => c.hex);

function store(key: string, value?: string): string | null {
  try {
    if (value !== undefined) localStorage.setItem(key, value);
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export class UI {
  readonly isTouch = matchMedia('(pointer: coarse)').matches;
  name: string;
  color: string;
  onEnter: () => void = () => {};
  onPlayPause: () => void = () => {};
  private hintTimer = 0;
  /** The controls hint is up and waits for the player to have moved for a while. */
  hintActive = false;

  constructor() {
    // default name: one of DEFAULT_NAMES (picked at random) and a number, e.g. "Observer 482" (2026-10-28; was
    // "Visitor NNN", before that "Guest NNN": an untouched old default is replaced, keeping its number).
    // names follow the server's rule (no control or bidi characters, 1..32): a name it rejects keeps the client
    // from joining (it reconnects with the same hello forever)
    const pick = () => DEFAULT_NAMES[Math.floor(Math.random() * DEFAULT_NAMES.length)]!;
    const stored = cleanText(store('lr3d.name')?.replace(/^(?:Guest|Visitor) (\d{3})$/, (_m, n: string) => `${pick()} ${n}`) ?? '', MAX_NAME_LENGTH);
    this.name = stored || `${pick()} ${Math.floor(100 + Math.random() * 900)}`;
    const c = store('lr3d.color');
    this.color = c && COLORS.includes(c) ? c : COLORS[Math.floor(Math.random() * COLORS.length)]!;

    const name = $<HTMLInputElement>('name');
    name.value = this.name;
    name.addEventListener('input', () => { this.name = cleanText(name.value, MAX_NAME_LENGTH) || this.name; });
    // colour dots: a radiogroup (one tab stop on the checked dot, arrows / Home / End move and select)
    const sw = $('swatches');
    const dots: HTMLButtonElement[] = [];
    const select = (i: number, focus: boolean) => {
      this.color = COLORS[i]!;
      dots.forEach((d, j) => {
        d.setAttribute('aria-checked', String(j === i));
        d.tabIndex = j === i ? 0 : -1;
      });
      if (focus) dots[i]!.focus();
    };
    PALETTE.forEach(({ name: label, hex }, i) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'swatch';
      b.setAttribute('role', 'radio');
      b.setAttribute('aria-label', label);
      b.title = label;
      b.style.setProperty('--c', hex);
      const dot = document.createElement('span');
      dot.className = 'swatch-dot';
      dot.setAttribute('aria-hidden', 'true');
      b.appendChild(dot);
      b.addEventListener('click', () => select(i, false));
      dots.push(b);
      sw.appendChild(b);
    });
    sw.addEventListener('keydown', (e) => {
      const i = dots.indexOf(e.target as HTMLButtonElement);
      if (i < 0) return;
      const n = dots.length;
      const next = e.key === 'ArrowRight' || e.key === 'ArrowDown' ? (i + 1) % n
        : e.key === 'ArrowLeft' || e.key === 'ArrowUp' ? (i - 1 + n) % n
        : e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : -1;
      if (next < 0) return;
      e.preventDefault();
      select(next, true);
    });
    select(Math.max(0, COLORS.indexOf(this.color)), false);
    // the card is filled now (name, colour dots): fade it in (style.css .card / .card.in); the next frame, so the
    // hidden state has been painted and the transition runs
    requestAnimationFrame(() => requestAnimationFrame(() => document.querySelector('#overlay .card')?.classList.add('in')));
    $('enter').addEventListener('click', () => {
      store('lr3d.name', this.name);
      store('lr3d.color', this.color);
      this.onEnter();
    });
    name.addEventListener('keydown', (e) => { if (e.key === 'Enter' && !($('enter') as HTMLButtonElement).disabled) $('enter').click(); });
    $('play').addEventListener('click', () => this.onPlayPause());
  }

  progress(f: number, text?: string): void {
    $('bar').style.width = `${Math.round(f * 100)}%`;
    if (text !== undefined) $('load-status').textContent = text;
  }

  ready(text: string): void {
    this.progress(1, text);
    document.querySelector('#overlay .card')?.classList.add('ready');
    const b = $<HTMLButtonElement>('enter');
    b.disabled = false;
    b.focus();
  }

  error(msg: string): void {
    const e = $('error');
    e.hidden = false;
    e.textContent = msg;
    $('load-status').textContent = '';
  }

  hideOverlay(): void {
    $('overlay').classList.add('gone');
    $('controls').hidden = false;
  }

  /** Controls hint after entering; stays until dismissHint() (the player has moved for a few seconds). */
  hint(): void {
    const h = $('hint');
    h.textContent = this.isTouch
      ? 'Left thumb to move · right thumb to look'
      : 'Arrows / WASD to move · drag to look';
    h.hidden = false;
    h.classList.remove('fade');
    clearTimeout(this.hintTimer);
    this.hintActive = true;
  }

  dismissHint(): void {
    if (!this.hintActive) return;
    this.hintActive = false;
    $('hint').classList.add('fade');
  }

  /** A short message in the hint's place (not while the controls hint is up). */
  notice(text: string, ms = 3500): void {
    if (this.hintActive) return;
    const h = $('hint');
    h.textContent = text;
    h.hidden = false;
    h.classList.remove('fade');
    clearTimeout(this.hintTimer);
    this.hintTimer = window.setTimeout(() => h.classList.add('fade'), ms);
  }

  private shownPlaying: boolean | null = null;

  /** The play button's icon (as Voyage: inline display on .play-icon / .pause-icon). No track name is shown. */
  media(playing: boolean): void {
    if (playing === this.shownPlaying) return;
    this.shownPlaying = playing;
    const p = $('play');
    (p.querySelector('.play-icon') as SVGElement).style.display = playing ? 'none' : 'block';
    (p.querySelector('.pause-icon') as SVGElement).style.display = playing ? 'block' : 'none';
  }

  net(status: string, peers: number): void {
    const n = $('net');
    n.hidden = status === 'off';
    n.className = status;
    $('net-text').textContent =
      status === 'online' ? `${peers + 1}`
      : status === 'connecting' ? 'connecting…'
      : status === 'reconnecting' ? 'reconnecting…'
      : status === 'limited' ? 'too many connections from your network'
      : 'offline';
    // online shows only the number (2026-10-28); the label says what it counts, for screen readers and on hover
    const label = status === 'online' ? `${peers + 1} ${peers === 0 ? 'visitor' : 'visitors'} in this room` : '';
    if (label) { n.setAttribute('aria-label', label); n.title = label; } else { n.removeAttribute('aria-label'); n.title = ''; }
  }
}
